import { useEffect, useMemo, useState } from 'react';

import type { BridgeView, Message } from '@/api/types';
import { checkSequence, peekMessage, toBase64Url, verifyMessage, type SequenceEntry, type SequenceProblem } from '@/crypto';
import { envelopeOf } from '@/lib/order';

export interface MessageOrder {
  /** The bridge as the person should see it: a repeated message dropped, the deletion marks that opened hidden. */
  readonly view: BridgeView | null;
  readonly problems: readonly SequenceProblem[];
}

/** What opening a message gave: the place it authenticated, and the place it removes if it is a deletion mark. */
interface Place {
  readonly sender: string;
  readonly seq: number;
}
interface Checked {
  readonly envelope: string;
  readonly position: Place | null;
  readonly removes: Place | null;
}

/** A writing device's name as text, to key a map by. */
const nameOf = (bytes: Uint8Array): string => toBase64Url(bytes);

/** A message listed twice under one id counts once: the first. */
function firstOfEach(messages: readonly Message[]): Message[] {
  const seen = new Set<string>();
  return messages.filter((message) => {
    if (seen.has(message.id)) return false;
    seen.add(message.id);
    return true;
  });
}

/** True when the envelope says it is a deletion mark; the byte is not authenticated, so this only picks what to open first. */
function claimsDeletion(message: Message): boolean {
  try {
    return peekMessage(envelopeOf(message)).deletion;
  } catch {
    return false;
  }
}

/**
 * Checks the list the server sent (docs/FORMAT.md §5): every writing device's messages must run from 0 without a hole, a
 * repeat or a step backwards, where a number the owner removed on purpose is covered by a deletion mark. Only what opened
 * counts: the place of a message is the one that was authenticated, never the one the envelope shows, and a mark is hidden
 * only once it has opened, so a flag or a number changed on the way leaves an unreadable message in the list and a hole in
 * the run, not a message that is gone. Until every message has been tried, nothing is reported.
 */
export function useMessageOrder(view: BridgeView | null, key: CryptoKey | null, bridgeId: string): MessageOrder {
  const [checked, setChecked] = useState<ReadonlyMap<string, Checked>>(new Map());

  useEffect(() => {
    if (key === null || view === null) return undefined;
    const todo = firstOfEach(view.messages).filter((message) => checked.get(message.id)?.envelope !== envelopeOf(message));
    if (todo.length === 0) return undefined;
    let cancelled = false;
    void Promise.all(
      todo.map(async (message): Promise<readonly [string, Checked]> => {
        const envelope = envelopeOf(message);
        try {
          const opened = await verifyMessage(key, envelope, { bridgeId, messageId: message.id, from: message.from }, message.kind);
          return [message.id, { envelope, position: { sender: nameOf(opened.position.sender), seq: opened.position.seq }, removes: opened.removes === null ? null : { sender: nameOf(opened.removes.sender), seq: opened.removes.seq } }];
        } catch {
          return [message.id, { envelope, position: null, removes: null }];
        }
      }),
    ).then((done) => {
      if (!cancelled) setChecked((before) => new Map([...before, ...done]));
    });
    return () => {
      cancelled = true;
    };
  }, [key, view, checked, bridgeId]);

  return useMemo(() => {
    if (view === null) return { view: null, problems: [] };
    const listed = firstOfEach(view.messages);
    const pending = listed.some((message) => checked.get(message.id)?.envelope !== envelopeOf(message));
    const entries: SequenceEntry[] = [];
    for (const message of listed) {
      const done = checked.get(message.id);
      if (!done?.position) continue;
      entries.push({ id: message.id, sender: done.position.sender, seq: done.position.seq, ...(done.removes ? { removes: done.removes } : {}) });
    }
    const report = checkSequence(entries);
    const messages = listed.filter((message) => {
      const done = checked.get(message.id);
      // an opened mark is not shown; one that has not been tried yet is kept back for a moment rather than flashed as a message
      const tried = done?.envelope === envelopeOf(message);
      return !(tried ? done.removes !== null : claimsDeletion(message));
    });
    // a message that does not open has no place to count, so a hole it leaves would be a guess: it is said outright
    const unreadable = listed.some((message) => checked.get(message.id)?.position === null);
    return { view: { ...view, messages }, problems: pending ? [] : unreadable ? [...report.problems, 'altered' as const] : report.problems };
  }, [view, checked]);
}
